import { useState, useEffect, useCallback } from "react";
import axiosVortex from "../../../utils/axios.instance";
import type { TransactionDetails } from "../../../models/orders";
import { getBrokerageExchange } from "../../../utils/helpers";
import { t } from "../../../utils/appString";
import { OptionTimeframe } from "../../../components/DashBoard/GTT/gttTypes";
import { OptionTimeframePayload } from "../../../components/DashBoard/GTT/gtt.helper";
import useSessionStorage from "../../../hooks/utils/setSessionStorage";

export interface OrderMarginProps {
  stockExchnage?: string;
  mktinstrumentType?: string;
  tradeType: OptionTimeframe;
  transactionType?: "SELL" | "BUY";
  price?: number;
  marketSegmentId: number;
}

const useOrderMargin = ({
  stockExchnage,
  mktinstrumentType,
  tradeType,
  transactionType,
  price,
  marketSegmentId,
}: OrderMarginProps) => {
  const [data, setData] = useState<TransactionDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [retryCount, setRetryCount] = useState<number>(0);

  const [instrumentType, setInstrumentType] = useState<string>("");
  const [productType, setProductType] = useState<string>("");

  const { setItem, getItem, removeItem } = useSessionStorage<TransactionDetails[]>();

  const exchange = getBrokerageExchange(marketSegmentId);

  const determineTypes = useCallback(() => {
    if (stockExchnage !== t("OrderConstants.nse_eq")) {
      if (
        mktinstrumentType === t("PE") ||
        mktinstrumentType === t("CE") ||
        mktinstrumentType === "OPT"
      ) {
        setInstrumentType(t("OPT"));
      } else {
        setInstrumentType(t("FUT"));
      }

      setProductType(
        tradeType === "CarryForward"
          ? "CARRYFORWARD"
          : OptionTimeframePayload(tradeType)
      );
    } else {
      setProductType(
        tradeType === "CarryForward"
          ? "CARRYFORWARD"
          : OptionTimeframePayload(tradeType)
      );
    }
  }, [stockExchnage, mktinstrumentType, tradeType]);

  const fetchData = useCallback(async () => {
    if (!exchange || !tradeType || !transactionType || !price) {
      console.error("useOrderMargin: Missing required parameters");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let consentratedData: TransactionDetails[] = getItem(t("keys.orderMarginSessionKey")) || [];

      if (consentratedData.length === 0) {
        // Fetch only if data not found in session storage
        const response = await axiosVortex.get("user/profile/brokerage");
        consentratedData = response.data.response as TransactionDetails[];

        // Save fetched data in session storage
        setItem(t("keys.orderMarginSessionKey"), consentratedData);
      }

      determineTypes();
      const filteredData = consentratedData.find((model) => {
        return (
          model.exchange === exchange &&
          model.instrument_type === instrumentType &&
          model.trade_type === productType.toUpperCase() &&
          model.transaction_type === transactionType &&
          (price ?? 0) >= model.value_from &&
          (price ?? 0) <=
            (model.value_to === -1 ? (price ?? 0) + 1 : model.value_to)
        );
      });

      if (filteredData) {
        setData(filteredData);
      } else {
        setError("No matching data found.");
      }
    } catch (err) {
      console.error("Error in useOrderMargin:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch brokerage data");

      if (retryCount < 3 && err instanceof Error && err.message.includes("network")) {
        setRetryCount((prev) => prev + 1);
        setTimeout(fetchData, 1000 * retryCount);
      }
    } finally {
      setLoading(false);
    }
  }, [exchange, tradeType, transactionType, price, retryCount, determineTypes, instrumentType, productType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};

export default useOrderMargin;
